// @givmo/board — EVERY word the board's self-assessment adds to the web (CBG-YR4-ASSESS-WEB): the
// Evaluations page, the start dialog (s1), the dashboard and its dialogs, the results and the board's
// priorities (s3), the person's own page (s2) and their row on "Your boards".
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// The words Jonathan approved in CBG-YR4-COPY1's self-assessment part (2026-10-15 10:32 PDT, "go with
// your recs and approved", `scratchpad/2026-10-15-assess-copy-round/copy-review.html`) are the
// backend's and mostly come off the wire: the instrument (its name, provenance line, statements,
// sources, the five answers, the comment prompt and help, the group titles, the open questions), the
// served sentences (the answers' fate, every refusal) and the record's fixed lines. Where a screen
// says one of them itself (the two mails' preview, the results' fixed lines, a refusal the page
// knows before it asks), it is here verbatim, marked "Approved".
//
// Every OTHER line is new on the web, and Jonathan approved them as drafted in CBG-YR4-COPY1's next
// part (2026-10-16 07:38 PDT, "go with your recs",
// `scratchpad/2026-10-15-assessweb-copy-round/copy-review.html`). Each names the frame that draws
// it (s1–s3) or says "not drawn". Four changed in CBG-FIX104 (2026-10-17 17:42 PDT, "1 and 2,
// approved.", `scratchpad/2026-10-17-fix104-copy-round/copy-review.html`): it asks only the people
// serving on the board, and the roster now holds committee members who aren't on it. Each says so.
//
// ⚠ Plan §3.2, §3.4 and D-CBG-28 bind every line: counts in the questions' own order, never a score,
// a rank, a strength or a weakness; the board chooses its own priorities by its own act; each
// person's answers are theirs alone while it is open, and nobody's are named after; nothing is
// "due", "overdue" or "late" beyond the date the board set; no line is about giving (D-CBG-4).

import { houseFormName } from '../../lib/house-form';
import { nameList } from '../conflicts/coi-words';

/** "1 person" · "2 people". */
function people(count: number): string {
  return count === 1 ? '1 person' : `${count} people`;
}

export const assessmentWords = {
  // ── The Self-assessment page ────────────────────────────────────────────────────────────────────

  // The page's name and its menu row. "Evaluations" (D-CBG-9 amended, CBG-YR4's Q4) shows as
  // "Self-assessment" (D-CBG-35 (1), Jonathan 2026-10-22 23:09 PDT; the word approved in the
  // wayfinding copy round, 2026-10-24 11:11 PDT).
  pageTitle: 'Self-assessment',

  // ── Before one starts (s1's page behind the dialog) ─────────────────────────────────────────────

  // Drawn s1.
  runTitle: 'The board’s self-assessment',
  // Drawn s1. CBG-FIX104 (row 3.9).
  runLine:
    'Everyone serving on the board answers the same questions about how the board works. Each person’s answers stay private to them; the board sees the results together.',
  // Drawn s1 (the button and the dialog's title).
  start: (label: string) => `Start the ${label} self-assessment`,
  // Drawn s1.
  earlier: 'Earlier self-assessments',
  // Drawn s1 (the derivation §3.7's row).
  earlierRow: (label: string, day: string, answered: number, asked: number) =>
    `${label} · results recorded ${day} · ${answered} of ${asked} answered`,
  // Not drawn: one withdrawn before anyone answered.
  earlierWithdrawn: (label: string, day: string) => `${label} · withdrawn ${day}`,
  // The page's line while the self-assessment isn't served here (its flag off, every environment since
  // CBG-YR4-ASSESS-DARK), where an address someone kept lands. Approved D-CBG-41 (Jonathan,
  // 2026-10-24 15:08 PDT): "yet" dropped, since it is dark until a customer asks for it.
  notServed: 'The board’s self-assessment isn’t available.',
  // Approved (CBG-YR4-COPY1, the backend's refusal for a type with no instrument), not drawn: said
  // on the page of an organization whose type is offered none, before anyone presses Start.
  notOffered: 'No starting self-assessment is offered for this organization’s type.',
  // Not drawn: a read failed.
  loadFailed: 'We couldn’t load the self-assessment just now.',
  // Approved (CBG-YR2-COPY1), reused.
  tryAgain: 'Try again',

  // ── The start dialog (s1) ───────────────────────────────────────────────────────────────────────

  // Drawn s1.
  nameField: 'Name',
  dueField: 'Answers due',
  privacyLine:
    'Each person’s answers are private to them. When you close it, the board sees how many people chose each answer, and the comments, without names. With fewer than three answers, no results are shown.',
  questionsLabel: 'The questions',
  // Not drawn: a switch's name for a screen reader.
  askStatement: (text: string) => `Ask: ${text}`,
  // Drawn s1 (derivation §3.3).
  whosAsked: 'Who’s asked',
  // CBG-FIX104 (row 3.10).
  whosAskedLine: 'Everyone serving on the board, unless you untick someone.',
  // Not drawn: who hasn't joined Board, and who has no email.
  notJoined: (names: readonly string[]) =>
    names.length === 1
      ? `${names[0]} hasn’t joined Board yet: that email includes an invitation to join.`
      : `${nameList(names)} haven’t joined Board yet: their emails include an invitation to join.`,
  noEmail: (names: readonly string[]) =>
    names.length === 1
      ? `${names[0]} has no email on the roster: you can record a paper answer sheet for them.`
      : `${nameList(names)} have no email on the roster: you can record a paper answer sheet for each.`,
  // Not drawn: nobody on the roster yet. CBG-FIX104 (row 3.7): a roster holding only committee
  // members who aren't on the board isn't empty, and none of them is asked.
  nobodyOnRoster: 'There’s nobody serving on the board yet. Add people under Board members first.',
  // Drawn s1 (cut off at the bottom of the frame).
  theEmail: 'The email each person gets',
  // Drawn s1.
  startAndSend: (count: number) => `Start and send to ${people(count)}`,
  // Approved (CBG-YR2-COPY1), reused.
  cancel: 'Cancel',
  // Approved (CBG-YR4-COPY1's served refusals), said beside the switches and the names as soon as
  // a choice would draw them.
  keepOneStatement: 'Keep at least one statement to rate.',
  askOnePerson: 'Ask at least one person.',
  // Not drawn: it went out.
  started: (label: string, sent: number) =>
    `The ${label} self-assessment is open. ${sent === 1 ? '1 email' : `${sent} emails`} sent.`,
  // Not drawn: the questions didn't load.
  questionsUnavailable: 'We couldn’t load the questions just now. Please try again.',
  // Approved (CBG-YR2-COPY1), reused.
  couldNotSave: 'We couldn’t save that. Please try again.',

  // ── The two mails, as the preview shows them (approved, CBG-YR4-COPY1) ─────────────────────────
  // The backend's own templates (`board_self_assessment_request_email.html`, `…_reminder_email.html`):
  // the preview shows what is sent. The sender, the greeting and the not-joined line are CBG-YR2-COPY1's,
  // reused there and here.

  emailFrom: (organization: string) => `From ${organization} Board <no-reply@givmocharitable.org>`,
  emailSubject: (label: string) => `Your board’s ${label} self-assessment`,
  emailHello: (name: string) => `Hello ${name},`,
  emailAsk: (organization: string, label: string) =>
    `${organization} asks everyone on its board to answer the board’s ${label} self-assessment.`,
  emailPleaseSendBy: (due: string) => `Please send your answers by ${due}.`,
  emailYoursDue: (due: string) => `Your answers are due ${due}.`,
  emailWasDue: (due: string) => `Your answers were due ${due}. You can still send them.`,
  emailPrivacy:
    'Your answers are private to you. The board sees the results together, without names, once it’s closed.',
  // CBG-FIX143: the mail's house form, "the {organization} board", a name's own leading "The"
  // dropped, as the backend's template sends it (CBG-UX-COPY2's fold, F1).
  emailNotJoined: (organization: string) =>
    `You’ll join the ${houseFormName(organization)} board in Givmo with your own account first.`,
  // Approved (CBG-YR4-COPY1, CBG-YR2-COPY1): an organization whose name doesn't resolve.
  emailYourOrganization: 'Your organization',
  emailNotJoinedUnnamed:
    'You’ll join your organization’s board in Givmo with your own account first.',
  emailButton: 'Open the self-assessment',

  // ── The open one (not drawn: the dashboard) ─────────────────────────────────────────────────────

  // Drawn s2 and s3 (a self-assessment's own title).
  title: (label: string) => `The ${label} self-assessment`,
  // Not drawn.
  openFacts: (due: string, opened: string, by: string | null) =>
    `Answers due ${due} · opened ${opened}${by ? ` by ${by}` : ''}`,
  // Not drawn ("6 of 7 answered", s3's own count; the figures bold).
  answeredOf: (answered: number, asked: number) => `${answered} of ${asked}`,
  answeredWord: 'answered',
  // Not drawn: behind the dashboard's "How this works". The second, CBG-FIX104 (row 3.11): a
  // committee member who isn't on the board isn't listed; one added to the board is.
  openHow: [
    'Everyone you asked answers on their own sign-in. Each person’s answers are private to them: you see who has answered, never what.',
    'Someone added to the board after it started is listed here for you to send it to.',
    'Reminders go only when you send them, at most once a day to each person.',
    'Closing it records the results in the minute book, and each person’s own answers are then deleted, unless a legal hold covers the board.',
  ],
  // Not drawn: the columns.
  personColumn: 'Person',
  statusColumn: 'Status',
  actionsColumn: 'Actions',
  // The derivation §3.4's words ("Answered" / "Not yet"), not drawn.
  statusAnswered: 'Answered',
  statusNotYet: 'Not yet',
  statusNotAsked: 'Not asked',
  // Approved (CBG-YR2-COPY1), reused: the same facts on a year's table.
  statusLeft: (on: string | null) => (on === null ? 'Left the board' : `Left the board ${on}`),
  hasntJoined: 'Hasn’t joined Board yet',
  noEmailOnRoster: 'No email on the roster',
  remindedOn: (on: string) => `Reminded ${on}`,
  joinedBoardOn: (on: string) => `Joined the board ${on}`,
  // Not drawn: answered on a paper sheet an officer marked.
  onPaper: 'On a paper answer sheet',
  // Approved (CBG-YR2-COPY1), reused: the year's word for a seat it hasn't gone to yet, here a seat
  // unticked at the start or added since.
  notSentYet: 'Not sent yet',
  // Approved (CBG-YR2-COPY1), reused.
  remindPeople: (count: number) => `Remind ${people(count)}`,
  send: 'Send',
  remind: 'Remind',
  actionsFor: (name: string) => `Actions for ${name}`,
  // Not drawn: the bulk send to everyone not sent it yet.
  sendPeople: (count: number) => `Send to ${people(count)}`,
  // Not drawn: the menu's accessible name and its items.
  menuFor: (label: string) => `More for the ${label} self-assessment`,
  recordSheet: 'Record a paper answer sheet',
  printQuestions: 'Print the questions',
  close: (label: string) => `Close the ${label} self-assessment`,
  withdraw: (label: string) => `Withdraw the ${label} self-assessment`,
  markPaper: 'Mark as handed in on paper',
  unmarkPaper: 'Undo the paper mark',
  // Not drawn: the toasts.
  markedPaper: (name: string) => `${name} is marked as having handed in a paper answer sheet.`,
  unmarkedPaper: (name: string) => `${name} is no longer marked.`,

  // ── Sending and reminding (not drawn) ───────────────────────────────────────────────────────────

  // New on the web.
  sendTitle: (names: readonly string[]) =>
    names.length === 1 ? `Send it to ${names[0]}` : `Send it to ${people(names.length)}`,
  sendLead: (label: string) =>
    `They weren’t sent the ${label} self-assessment when it started. Each gets one email asking for their answers.`,
  sendLeadOne: (label: string) =>
    `They weren’t sent the ${label} self-assessment when it started. They get one email asking for their answers.`,
  // New on the web.
  sentTo: (sent: number) => (sent === 1 ? 'Sent to 1 person.' : `Sent to ${sent} people.`),
  // Approved (CBG-YR2-COPY1), reused.
  includesInvitation: 'The email includes an invitation to join Board.',
  remindTitle: (names: readonly string[]) =>
    names.length === 1 ? `Remind ${names[0]}` : `Remind ${people(names.length)}`,
  // New on the web.
  remindLead: (label: string) =>
    `They haven’t answered the ${label} self-assessment yet. Each gets one email.`,
  remindLeadOne: (label: string) =>
    `They haven’t answered the ${label} self-assessment yet. They get one email.`,
  // Approved (CBG-YR2-COPY1), reused.
  lastReminded: (on: string) => `Last reminded ${on}`,
  sentOn: (on: string) => `Sent ${on}`,
  sendReminders: (count: number) => (count === 1 ? 'Send the reminder' : `Send ${count} reminders`),
  remindersSent: (sent: number) => (sent === 1 ? '1 reminder sent.' : `${sent} reminders sent.`),

  // ── Closing and withdrawing (not drawn) ─────────────────────────────────────────────────────────

  // New on the web.
  closeCount: (answered: number, asked: number) => `${answered} of ${asked} answered.`,
  closeRecord:
    'Closing it records the results in the minute book: how many people chose each answer, and the comments, without names. Each person’s own answers are then deleted, unless a legal hold covers the board.',
  closeUnderThree:
    'Fewer than three people have answered, so the record will say only how many were asked and how many answered.',
  closeNoMore: 'Nobody can answer it once it’s closed.',
  closeAndRecord: 'Close and record the results',
  closed: (label: string) => `The ${label} self-assessment is closed. Its results are recorded.`,
  withdrawLead:
    'Nobody has answered it yet. Withdrawing it records nothing in the minute book, and no one is emailed.',
  withdrawButton: 'Withdraw it',
  withdrawn: (label: string) => `The ${label} self-assessment was withdrawn.`,

  // ── Paper (not drawn) ───────────────────────────────────────────────────────────────────────────

  // New on the web.
  sheetLead:
    'Enter one sheet as it was filled in. A sheet carries no name, so nothing here ties its answers to anyone.',
  sheetInferable:
    'With only one paper sheet, who wrote it can be worked out from who handed one in.',
  sheetWaiting: (waiting: number) =>
    waiting === 1
      ? 'One person is marked as having handed in a sheet not yet entered.'
      : `${waiting} people are marked as having handed in sheets not yet entered.`,
  recordThisSheet: 'Record this sheet',
  sheetRecorded: 'The answer sheet is recorded.',
  // The printed sheet.
  printHeading: (label: string) => `The ${label} self-assessment`,
  printLine:
    'Mark one answer for each statement you answer. Please don’t write your name on this sheet.',
  printDue: (due: string) => `Answers due ${due}`,
  couldNotPrint: 'We couldn’t load the questions to print just now. Please try again.',

  // ── The results and the board's priorities (s3) ─────────────────────────────────────────────────

  // Drawn s3.
  resultsLine: (closed: string, answered: number, asked: number) =>
    `Closed ${closed} · ${answered} of ${asked} answered · `,
  inMinuteBook: 'the results are in the minute book',
  // Not drawn: the way back from one assessment.
  allSelfAssessments: 'All self-assessments',
  // Approved (CBG-YR4-COPY1, the record's fixed lines), reused.
  underThree: 'Fewer than three people answered, so no results are shown.',
  noAnswers: 'No answers.',
  comments: 'Comments',
  // Drawn s3.
  prioritiesTitle: 'The board’s priorities',
  prioritiesAtMeeting: (meeting: string, day: string) =>
    `Chosen by the board at the ${meeting}, ${day}.`,
  // Not drawn: chosen by written consent.
  prioritiesByConsent: (day: string) => `Chosen by the board by written consent on ${day}.`,
  // Not drawn: a written consent the read can't date (it is no longer kept).
  prioritiesByAConsent: 'Chosen by the board by written consent.',
  // Not drawn: a motion whose meeting the read can't name.
  prioritiesByMotion: 'Chosen by the board by a motion it carried.',
  // Drawn s3.
  statementsNamed: 'Statements the board named',
  // The derivation §3.6, not drawn.
  recordPriorities: 'Record the board’s priorities',
  changePriorities: 'Link a different act',

  // ── Linking the priorities (not drawn) ──────────────────────────────────────────────────────────

  // New on the web.
  linkLead:
    'Link the motion the board carried, or the written consent every director signed, that chose the board’s priorities.',
  aMotion: 'A motion',
  aConsent: 'A written consent',
  meetingField: 'The meeting',
  motionField: 'The motion',
  consentField: 'The written consent',
  noCarriedMotions: 'No motion carried at this meeting can be linked.',
  noExecutedConsents: 'No written consent has been signed by every director yet.',
  statementsField: 'The statements it names (optional)',
  linkIt: 'Link it',
  linked: 'The board’s priorities are linked.',
  chooseMeeting: 'Choose a meeting',

  // ── The person's own page (s2) ──────────────────────────────────────────────────────────────────

  // Approved (CBG-YR4-COPY1, the ask's privacy line), reused on the page that the ask opens: s2
  // drew it as "… once the self-assessment is closed."; the approved mail's words say the same.
  directorLead:
    'Your answers are private to you. The board sees the results together, without names, once it’s closed.',
  // Drawn s2.
  directorDue: (day: string) => `Answers due ${day}`,
  sendAnswers: 'Send my answers',
  // s2's "Your answers stay on this device until you send them." is not built: unsent answers stay
  // in this tab, for a day at most (the drafts module's rule), so the line promised more than is
  // kept, and the year's questionnaire, on the same drafts, says nothing of them (the cut's one
  // thing removed).
  // Not drawn: sent, and changeable until the close.
  sentAnswersOn: (day: string) =>
    `You sent your answers on ${day}. You can change them until it’s closed.`,
  // The toast says only that they went: the line above says the rest (the cut's one thing removed).
  answersSent: 'Your answers are sent.',
  // Approved (CBG-YR4-COPY1, the served refusals), reused for the states the page reads first.
  answerAtLeastOne: 'Answer at least one question.',
  paperRecorded: 'Your paper answer sheet is already recorded.',
  withdrawnServed: 'This self-assessment was withdrawn.',
  // The later holder of a seat whose answers an earlier holder sent, on an open one (contract §4.1,
  // C21): they neither read nor replace those answers.
  earlierHolderServed: 'This seat’s answers were sent before you joined the board.',
  // Not drawn: the way back to the person's boards.
  yourBoards: 'Your boards',
  // Not drawn: the page's title once its reads have settled with no self-assessment
  // to name (not asked, not served, none, a read that failed), never a loading bar left for good.
  directorPageTitle: 'Your board’s self-assessment',
  // Not drawn: the read failed for no refusal.
  directorUnavailable: 'We couldn’t load your self-assessment just now.',
  // Not drawn: nothing this seat was asked, or one it wasn't asked (the backend's
  // not-found, which says nothing of what anyone else was asked).
  noneForYou: 'There’s no self-assessment here for you to answer.',

  // ── The person's row on "Your boards" (derivation §5) ───────────────────────────────────────────

  // The derivation §5's words, not drawn.
  attentionRow: (label: string, due: string) =>
    `Your board’s ${label} self-assessment · due ${due}`,

  // ── The minute book (the close's record) ────────────────────────────────────────────────────────

  // Approved (CBG-YR4-COPY1): the kind's words, as the readable copy prints them.
  kindLabel: 'Self-assessment results',
  // Not drawn: the close's line in the record's history, as a year's annual
  // disclosures say theirs.
  closedEvent: 'Self-assessment closed',
} as const;
